import { MdAccessTime, MdDateRange, MdDoneAll, MdOutlineFileCopy, MdOutlineMap } from 'react-icons/md';
import { Button, cn, TmdxAvatar, useDisclosure } from '@ds';
import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import TabSection from '@features/components/CaseOverview/components/TabSection/TabSection';
import { SectionMiniCard } from '../components/SectionMiniCard';
import { CaseResourceCombined, OrganType, SurgeonCaseSummary } from '@/api/apis/case';
import { formatDate, getFullName, ORGAN_ICON } from '@/utils/helpers';
import { SurgeonCaseSummaryModal } from '../components/SurgeonCaseSummaryModal/SurgeonCaseSummaryModal';
import { CaseDetailsPermissions } from '@/permissions';

type SurgeonCaseSummaryProps = {
  unosId: string;
  organType: OrganType;
  surgeon?: CaseResourceCombined;
  surgeonCaseSummary?: SurgeonCaseSummary;
};

export const SurgeonCaseSummaryCard = ({ unosId, organType, surgeon, surgeonCaseSummary }: SurgeonCaseSummaryProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const OrganIcon = ORGAN_ICON[organType];
  const { checkPermission } = useContext(AuthContext);
  const hasViewSurgeonFormPermission = checkPermission(CaseDetailsPermissions.SurgeonCompletionRead);

  return (
    <TabSection title="Surgeon Summary">
      <SectionMiniCard icon={<MdOutlineFileCopy size={24} />} title="Surgeon Case Summary">
        {organType === OrganType.Lungs ? (
          <div className="flex flex-1 items-center flex-col">
            <OrganIcon className="text-base-60 text-7xl size-[55px]" />
            <div className="text-s-r text-base-35">Form is not required</div>
          </div>
        ) : (
          <>
            <div className="flex flex-1 flex-col bg-base-90 rounded-lg p-3">
              <div className="flex justify-between">
                <span className={`text-sm text-base-30 ${cn({ 'text-base-10! font-semibold': surgeonCaseSummary })}`}>
                  {unosId}
                </span>
                {surgeonCaseSummary && <MdDoneAll size={14} className="text-success-30 w-[30px] h-[20px]" />}
              </div>
              <div className="flex gap-3 mt-2">
                <div
                  className={`flex text-sm text-base-30 ${cn({ 'text-base-20!': surgeonCaseSummary })} items-center gap-1`}
                >
                  <MdDateRange size={14} />
                  {surgeonCaseSummary
                    ? formatDate(surgeonCaseSummary.modifiedAt, 'MM/dd/yyyy HH:mm zzz').split(' ')[0]
                    : '--:--'}
                </div>
                <div
                  className={`flex text-sm text-base-30 ${cn({ 'text-base-20!': surgeonCaseSummary })} items-center gap-1`}
                >
                  <MdAccessTime size={14} />
                  {surgeonCaseSummary
                    ? formatDate(surgeonCaseSummary.modifiedAt, 'MM/dd/yyyy HH:mm zzz').split(' ')[1]
                    : '--:--'}
                </div>
                <div
                  className={`flex text-sm text-base-30 ${cn({ 'text-base-20!': surgeonCaseSummary })} items-center gap-1`}
                >
                  <MdOutlineMap size={14} />
                  {surgeonCaseSummary
                    ? formatDate(surgeonCaseSummary.modifiedAt, 'MM/dd/yyyy HH:mm zzz').split(' ')[2]
                    : '--:--'}
                </div>
              </div>
            </div>
            <div className="flex items-center text-base-30 text-sm font-medium justify-between">
              {surgeon ? (
                <div className="flex gap-2 items-center">
                  <TmdxAvatar size="sm" radius="sm" src={surgeon.resourceData?.profilePhoto} />
                  {getFullName(surgeon.resourceData?.firstName, surgeon.resourceData?.lastName)}
                </div>
              ) : (
                <div className="flex gap-2 items-center">
                  <TmdxAvatar size="sm" radius="sm" type="retrieval-surgeon" />
                  Retrieval Surgeon
                </div>
              )}

              {hasViewSurgeonFormPermission && (
                <Button
                  className="h-6 min-w-0 px-2 text-xs-sb!"
                  variant="outline"
                  onClick={onOpen}
                  isDisabled={!surgeonCaseSummary}
                >
                  View Form
                </Button>
              )}
            </div>
          </>
        )}
      </SectionMiniCard>
      {surgeonCaseSummary && (
        <SurgeonCaseSummaryModal
          onOpen={onOpen}
          isOpen={isOpen}
          onClose={onClose}
          surgeonCaseSummaryData={surgeonCaseSummary.summary}
          organType={organType}
        />
      )}
    </TabSection>
  );
};
